import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import isEmpty from 'lodash/isEmpty';
import { cloneDeep, pick } from 'lodash';
import validateForm, { validate } from 'validate.js';

// to perform validation with date fields
dayjs.extend(utc);

// helper method to preformat the form before validation, '' is considered valid in validate.js - should be null
const preFormatForm = (formObj) => {
  let newFormObj = JSON.parse(JSON.stringify(formObj));
  nullifyEmptyStrings(newFormObj);
  return newFormObj;
};

const trimInputValues = (formObj) => {
  let newFormObj = JSON.parse(JSON.stringify(formObj));
  trimStrings(newFormObj);
  return newFormObj;
};

const trimStrings = (obj) => {
  for (let [key, value] of Object.entries(obj)) {
    if (validateForm.isString(value)) {
      obj[key] = value.trim();
    } else if (validateForm.isObject(value)) {
      trimStrings(value);
    }
  }
};

const nullifyEmptyStrings = (obj) => {
  for (let [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && !value.trim()) {
      obj[key] = null;
    } else if (validateForm.isHash(value)) {
      nullifyEmptyStrings(value);
    } else if (validateForm.isArray(value)) {
      value.map((item) => {
        if (typeof item === 'string' || typeof item === 'number') {
          nullifyEmptyStrings({
            name: item
          });
        } else {
          nullifyEmptyStrings(item);
        }
      });
    }
  }
};

const isEmptyForm = (obj, discardFields) => {
  for (let [key, value] of Object.entries(obj)) {
    if (!discardFields.includes(key) && value) {
      return false;
    }
  }
  return true;
};

// Before using it we must add the parse and format functions for date/time fields
validateForm.extend(validateForm.validators.datetime, {
  parse: function(value, options) {
    return dayjs.utc(value);
  },
  format: function(value, options) {
    var format = options.dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD hh:mm:ss';
    return dayjs.utc(value, format);
  }
});

// Custom validator for array - no support in validate.js

validateForm.validators.array = (arrayItems, itemConstraints) => {
  if (itemConstraints?.presence && arrayItems?.length === 0) {
    return "can't be empty";
  }
  const arrayItemErrors = arrayItems?.reduce((errors, item, index) => {
    const { presence, ...otherConstraints } = itemConstraints;
    let error;
    if (typeof item === 'object') error = validateForm(item, otherConstraints);
    else if (typeof item === 'number' || typeof item === 'string')
      error = validate.single(
        item,
        otherConstraints?.value || otherConstraints
      );
    if (error) errors[index] = { error: error };
    return errors;
  }, {});

  return isEmpty(arrayItemErrors) ? null : { errors: arrayItemErrors };
};

validateForm.validators.dynamicArray = (arrayItems, itemConstraints) => {
  if (itemConstraints?.presence && arrayItems?.length === 0) {
    return "can't be empty";
  }
  const arrayItemErrors = arrayItems?.reduce((errors, item, index) => {
    const { presence, ...otherConstraints } = itemConstraints;
    let error;
    if (typeof item === 'object') {
      const keys = Object.keys(item);
      const clonedConstrains = cloneDeep(otherConstraints);
      const trimmedConstrains = pick(clonedConstrains, keys);
      error = validateForm(item, trimmedConstrains);
    } else if (typeof item === 'number' || typeof item === 'string')
      error = validate.single(item, otherConstraints);
    if (error) errors[index] = { error: error };
    return errors;
  }, {});

  return isEmpty(arrayItemErrors) ? null : { errors: arrayItemErrors };
};

validateForm.validators.regexChecker = (
  value,
  options,
  key,
  attributes,
  globalOptions
) => {
  const { regex, mandatory, field } = options;
  if (mandatory && !value) {
    if (field) {
      return `^${field} can't be blank`;
    }
    return "can't be blank";
  }
  if (!regex.test(value)) {
    if (field) {
      return `^${field} is not valid`;
    }
    return 'is not valid';
  }
};

validateForm.validators.type.types.isEmpty = function(value) {
  return !validateForm.isEmpty(value);
};

validateForm.validators.atLeastOneMinuteAhead = function(value, options) {
  if (options?.mandatory && !value) {
    return {
      key: 'validation.expiryDate.empty',
      field: options.field,
      defaultMessage: `${options.field} can't be blank`
    };
  }

  const now = dayjs();
  const inputTime = dayjs(value);

  if (!inputTime.isValid()) {
    return {
      key: 'dromo.validation.invalid.value',
      field: options.field,
      defaultMessage: `${options.field} is not valid`
    };
  }

  if (inputTime.isBefore(now.add(1, 'minute'))) {
    return {
      key: 'common.validation.future.datetime',
      field: options.field,
      defaultMessage: `${options.field} must be at least 1 minute ahead`
    };
  }

  return null;
};

export { validateForm, preFormatForm, trimInputValues, isEmptyForm };
